'use client';

import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type DrawerSide = 'left' | 'right' | 'top' | 'bottom';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface DrawerContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  side: DrawerSide;
}

// ============================================================================
// Context
// ============================================================================

const DrawerContext = React.createContext<DrawerContextValue | null>(null);

export function useDrawerContext() {
  const context = React.useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawerContext must be used within Drawer');
  }
  return context;
}

// ============================================================================
// Drawer
// ============================================================================

export interface DrawerProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: DrawerSide;
  children: React.ReactNode;
}

export function Drawer({
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  side = 'right',
  children,
}: DrawerProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [isControlled, onOpenChange]
  );

  const contextValue: DrawerContextValue = {
    open,
    setOpen,
    side,
  };

  return (
    <DrawerContext.Provider value={contextValue}>
      {children}
    </DrawerContext.Provider>
  );
}

// ============================================================================
// DrawerTrigger
// ============================================================================

export interface DrawerTriggerProps {
  children: React.ReactElement;
  asChild?: boolean;
}

export function DrawerTrigger({ children, asChild = false }: DrawerTriggerProps) {
  const { setOpen } = useDrawerContext();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(true);
  };

  if (React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: handleClick,
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <button type="button" onClick={handleClick}>
      {children}
    </button>
  );
}

// ============================================================================
// DrawerPortal
// ============================================================================

export interface DrawerPortalProps {
  children: React.ReactNode;
  container?: HTMLElement;
}

export function DrawerPortal({ children }: DrawerPortalProps) {
  const { open } = useDrawerContext();

  if (!open) return null;

  return <>{children}</>;
}

// ============================================================================
// DrawerOverlay
// ============================================================================

export interface DrawerOverlayProps extends React.HTMLAttributes<HTMLDivElement> {}

export function DrawerOverlay({ className, ...props }: DrawerOverlayProps) {
  const { setOpen } = useDrawerContext();

  return (
    <div
      className={clsx(
        'drawer-overlay fixed inset-0 z-50',
        'bg-black/50 backdrop-blur-sm',
        'animate-in fade-in duration-200',
        className
      )}
      onClick={() => setOpen(false)}
      aria-hidden="true"
      {...props}
    />
  );
}

// ============================================================================
// DrawerContent
// ============================================================================

export interface DrawerContentProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: DrawerSize;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
}

export function DrawerContent({
  size = 'md',
  closeOnEscape = true,
  closeOnOverlayClick = true,
  className,
  children,
  ...props
}: DrawerContentProps) {
  const { open, setOpen, side } = useDrawerContext();
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open || !closeOnEscape) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeOnEscape, setOpen]);

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      contentRef.current?.focus();

      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  if (!open) return null;

  const sideClasses: Record<DrawerSide, string> = {
    left: 'inset-y-0 left-0',
    right: 'inset-y-0 right-0',
    top: 'inset-x-0 top-0',
    bottom: 'inset-x-0 bottom-0',
  };

  const sizeClasses: Record<DrawerSide, Record<DrawerSize, string>> = {
    left: {
      sm: 'w-64',
      md: 'w-80',
      lg: 'w-96',
      xl: 'w-[480px]',
      full: 'w-full',
    },
    right: {
      sm: 'w-64',
      md: 'w-80',
      lg: 'w-96',
      xl: 'w-[480px]',
      full: 'w-full',
    },
    top: {
      sm: 'h-48',
      md: 'h-64',
      lg: 'h-80',
      xl: 'h-96',
      full: 'h-full',
    },
    bottom: {
      sm: 'h-48',
      md: 'h-64',
      lg: 'h-80',
      xl: 'h-96',
      full: 'h-full',
    },
  };

  const animationClasses: Record<DrawerSide, string> = {
    left: 'animate-in slide-in-from-left duration-300',
    right: 'animate-in slide-in-from-right duration-300',
    top: 'animate-in slide-in-from-top duration-300',
    bottom: 'animate-in slide-in-from-bottom duration-300',
  };

  return (
    <>
      <DrawerOverlay onClick={closeOnOverlayClick ? undefined : (e) => e.stopPropagation()} />
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={clsx(
          'drawer-content fixed z-50',
          'bg-white dark:bg-gray-800',
          'border-gray-200 dark:border-gray-700',
          'shadow-xl',
          'flex flex-col',
          sideClasses[side],
          sizeClasses[side][size],
          side === 'left' && 'border-r',
          side === 'right' && 'border-l',
          side === 'top' && 'border-b',
          side === 'bottom' && 'border-t',
          animationClasses[side],
          className
        )}
        {...props}
      >
        {children}
      </div>
    </>
  );
}

// ============================================================================
// DrawerHeader
// ============================================================================

export interface DrawerHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

export function DrawerHeader({ className, ...props }: DrawerHeaderProps) {
  return (
    <div
      className={clsx(
        'drawer-header flex items-center justify-between px-4 py-3',
        'border-b border-gray-200 dark:border-gray-700',
        className
      )}
      {...props}
    />
  );
}

// ============================================================================
// DrawerTitle
// ============================================================================

export interface DrawerTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {}

export function DrawerTitle({ className, ...props }: DrawerTitleProps) {
  return (
    <h2
      className={clsx(
        'drawer-title text-lg font-semibold',
        'text-gray-900 dark:text-gray-100',
        className
      )}
      {...props}
    />
  );
}

// ============================================================================
// DrawerDescription
// ============================================================================

export interface DrawerDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

export function DrawerDescription({ className, ...props }: DrawerDescriptionProps) {
  return (
    <p
      className={clsx(
        'drawer-description text-sm text-gray-500 dark:text-gray-400',
        className
      )}
      {...props}
    />
  );
}

// ============================================================================
// DrawerBody
// ============================================================================

export interface DrawerBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

export function DrawerBody({ className, ...props }: DrawerBodyProps) {
  return (
    <div
      className={clsx('drawer-body flex-1 overflow-auto p-4', className)}
      {...props}
    />
  );
}

// ============================================================================
// DrawerFooter
// ============================================================================

export interface DrawerFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export function DrawerFooter({ className, ...props }: DrawerFooterProps) {
  return (
    <div
      className={clsx(
        'drawer-footer flex items-center justify-end gap-2 px-4 py-3',
        'border-t border-gray-200 dark:border-gray-700',
        className
      )}
      {...props}
    />
  );
}

// ============================================================================
// DrawerClose
// ============================================================================

export interface DrawerCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

export function DrawerClose({
  asChild = false,
  children,
  className,
  onClick,
  ...props
}: DrawerCloseProps) {
  const { setOpen } = useDrawerContext();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    setOpen(false);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: handleClick,
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <button
      type="button"
      className={clsx(
        'drawer-close p-1 rounded',
        'hover:bg-gray-100 dark:hover:bg-gray-700',
        'transition-colors',
        className
      )}
      onClick={handleClick}
      aria-label="Close"
      {...props}
    >
      {children || (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      )}
    </button>
  );
}

// ============================================================================
// Sheet (Convenience Wrapper)
// ============================================================================

export interface SheetProps {
  open?: boolean;
  onClose?: () => void;
  side?: DrawerSide;
  size?: DrawerSize;
  title?: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function Sheet({
  open = false,
  onClose,
  side = 'right',
  size = 'md',
  title,
  description,
  children,
  footer,
  className,
}: SheetProps) {
  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()} side={side}>
      <DrawerContent size={size} className={className}>
        {(title || description) && (
          <DrawerHeader>
            <div>
              {title && <DrawerTitle>{title}</DrawerTitle>}
              {description && <DrawerDescription>{description}</DrawerDescription>}
            </div>
            <DrawerClose />
          </DrawerHeader>
        )}
        <DrawerBody>{children}</DrawerBody>
        {footer && <DrawerFooter>{footer}</DrawerFooter>}
      </DrawerContent>
    </Drawer>
  );
}

// ============================================================================
// NavigationDrawer
// ============================================================================

export interface NavigationItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  active?: boolean;
  children?: NavigationItem[];
}

export interface NavigationDrawerProps {
  open?: boolean;
  onClose?: () => void;
  items: NavigationItem[];
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

export function NavigationDrawer({
  open = false,
  onClose,
  items,
  header,
  footer,
}: NavigationDrawerProps) {
  const handleItemClick = (item: NavigationItem) => {
    item.onClick?.();
    if (!item.children) {
      onClose?.();
    }
  };

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()} side="left">
      <DrawerContent size="sm" className="navigation-drawer">
        {header && (
          <DrawerHeader>
            {header}
            <DrawerClose />
          </DrawerHeader>
        )}

        <DrawerBody className="p-0">
          <nav className="py-2">
            {items.map((item) => (
              <div key={item.id}>
                <button
                  type="button"
                  onClick={() => handleItemClick(item)}
                  className={clsx(
                    'w-full flex items-center gap-3 px-4 py-3',
                    'text-left text-sm',
                    'transition-colors',
                    item.active
                      ? 'bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  )}
                >
                  {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                  <span className="flex-1">{item.label}</span>
                </button>

                {item.children && (
                  <div className="ml-8 border-l border-gray-200 dark:border-gray-700">
                    {item.children.map((child) => (
                      <button
                        key={child.id}
                        type="button"
                        onClick={() => handleItemClick(child)}
                        className={clsx(
                          'w-full flex items-center gap-3 px-4 py-2',
                          'text-left text-sm',
                          'transition-colors',
                          child.active
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        )}
                      >
                        {child.icon && <span className="flex-shrink-0">{child.icon}</span>}
                        <span>{child.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </DrawerBody>

        {footer && <DrawerFooter>{footer}</DrawerFooter>}
      </DrawerContent>
    </Drawer>
  );
}

// ============================================================================
// FilterDrawer
// ============================================================================

export interface FilterSection {
  id: string;
  title: string;
  content: React.ReactNode;
  expanded?: boolean;
}

export interface FilterDrawerProps {
  open?: boolean;
  onClose?: () => void;
  onApply?: () => void;
  onReset?: () => void;
  title?: string;
  sections: FilterSection[];
  hasChanges?: boolean;
}

export function FilterDrawer({
  open = false,
  onClose,
  onApply,
  onReset,
  title = 'Filters',
  sections,
  hasChanges = false,
}: FilterDrawerProps) {
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(
    new Set(sections.filter((s) => s.expanded !== false).map((s) => s.id))
  );

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleApply = () => {
    onApply?.();
    onClose?.();
  };

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()} side="right">
      <DrawerContent size="sm" className="filter-drawer">
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <button
                type="button"
                onClick={onReset}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Reset
              </button>
            )}
            <DrawerClose />
          </div>
        </DrawerHeader>

        <DrawerBody className="p-0">
          {sections.map((section) => (
            <div
              key={section.id}
              className="border-b border-gray-200 dark:border-gray-700 last:border-b-0"
            >
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                className={clsx(
                  'w-full flex items-center justify-between px-4 py-3',
                  'text-left text-sm font-medium',
                  'text-gray-900 dark:text-gray-100',
                  'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                )}
              >
                <span>{section.title}</span>
                <svg
                  className={clsx(
                    'w-4 h-4 transition-transform',
                    expandedSections.has(section.id) && 'rotate-180'
                  )}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {expandedSections.has(section.id) && (
                <div className="px-4 pb-4">{section.content}</div>
              )}
            </div>
          ))}
        </DrawerBody>

        <DrawerFooter>
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium',
              'border border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-700',
              'text-gray-700 dark:text-gray-200',
              'hover:bg-gray-50 dark:hover:bg-gray-600'
            )}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium',
              'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            Apply Filters
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

// ============================================================================
// DetailsDrawer
// ============================================================================

export interface DetailsDrawerProps {
  open?: boolean;
  onClose?: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

export function DetailsDrawer({
  open = false,
  onClose,
  title,
  subtitle,
  children,
  actions,
}: DetailsDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()} side="right">
      <DrawerContent size="lg" className="details-drawer">
        <DrawerHeader>
          <div>
            {title && <DrawerTitle>{title}</DrawerTitle>}
            {subtitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {subtitle}
              </p>
            )}
          </div>
          <DrawerClose />
        </DrawerHeader>

        <DrawerBody>{children}</DrawerBody>

        {actions && <DrawerFooter>{actions}</DrawerFooter>}
      </DrawerContent>
    </Drawer>
  );
}

// ============================================================================
// MobileMenu
// ============================================================================

export interface MobileMenuProps {
  open?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}

export function MobileMenu({ open = false, onClose, children }: MobileMenuProps) {
  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()} side="left">
      <DrawerContent size="full" className="mobile-menu">
        <DrawerHeader>
          <DrawerTitle>Menu</DrawerTitle>
          <DrawerClose />
        </DrawerHeader>
        <DrawerBody className="p-0">{children}</DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

// ============================================================================
// useDrawer Hook
// ============================================================================

export interface UseDrawerOptions {
  defaultOpen?: boolean;
  side?: DrawerSide;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface UseDrawerReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  getDrawerProps: () => DrawerProps;
  getTriggerProps: () => {
    onClick: () => void;
  };
}

export function useDrawer(options: UseDrawerOptions = {}): UseDrawerReturn {
  const { defaultOpen = false, side = 'right', onOpen, onClose } = options;
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const open = React.useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const close = React.useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const toggle = React.useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  const getDrawerProps = React.useCallback(
    (): DrawerProps => ({
      open: isOpen,
      onOpenChange: (newOpen) => {
        if (newOpen) {
          open();
        } else {
          close();
        }
      },
      side,
      children: null as unknown as React.ReactNode,
    }),
    [isOpen, open, close, side]
  );

  const getTriggerProps = React.useCallback(
    () => ({
      onClick: open,
    }),
    [open]
  );

  return {
    isOpen,
    open,
    close,
    toggle,
    getDrawerProps,
    getTriggerProps,
  };
}
